import React, { useState, useEffect } from 'react';
import ContractViewer from './ContractViewer';
import { secureFetch } from './common';

const GTM_API_URL = process.env.REACT_APP_GTM_API_URL || 'https://newton-gtm.vercel.app';
const EXPRESS_API_URL = process.env.REACT_APP_DENTAL_API_URL || 'https://dentalexpressserver.azurewebsites.net';

const statusColors = {
  draft: { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
  sent: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
  accepted: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
};

const QuotesList = ({ embedded = false, onEdit }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewerQuote, setViewerQuote] = useState(null); // quote whose contract is open in the in-app viewer
  const [sendQuote, setSendQuote] = useState(null); // quote open in the send-for-signature modal
  const [sendName, setSendName] = useState('');
  const [sendTitle, setSendTitle] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const [sendBusy, setSendBusy] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [sendErr, setSendErr] = useState('');

  const openSendModal = (quote) => {
    setSendQuote(quote);
    setSendName(quote.signerName || [quote.billingFirstName, quote.billingLastName].filter(Boolean).join(' '));
    setSendTitle(quote.signerTitle || '');
    setSendEmail(quote.signerEmail || quote.billingEmail || '');
    setSendMsg('');
    setSendErr('');
  };

  const handleSendSignature = async () => {
    if (!sendQuote) return;
    setSendErr('');
    if (!sendName.trim()) { setSendErr('Signer name is required'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendEmail.trim())) { setSendErr('A valid signer email is required'); return; }
    setSendBusy(true);
    try {
      const res = await secureFetch(`${EXPRESS_API_URL}/api/quotes/${sendQuote.quoteId}/send-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signerName: sendName.trim(), signerTitle: sendTitle.trim() || undefined, signerEmail: sendEmail.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setSendMsg(`Sent to ${data.sentTo}${data.testMode ? ' (test mode)' : ''}`);
        setQuotes(prev => prev.map(q => q.quoteId === sendQuote.quoteId
          ? { ...q, status: q.status === 'draft' ? 'sent' : q.status, signatureSentAt: new Date().toISOString(), signerName: sendName.trim(), signerTitle: sendTitle.trim(), signerEmail: sendEmail.trim() }
          : q));
      } else {
        setSendErr(data.error || 'Failed to send');
      }
    } catch (err) {
      setSendErr(err.message || 'Failed to send');
    } finally {
      setSendBusy(false);
    }
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const params = filter !== 'all' ? `?status=${filter}` : '';
        const res = await secureFetch(`${EXPRESS_API_URL}/api/quotes${params}`);
        const data = await res.json();
        if (data.success) {
          setQuotes((data.quotes || []).filter(q => !q.test));
        } else {
          setError(data.error || 'Failed to fetch quotes');
        }
      } catch (err) {
        setError('Failed to fetch quotes');
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [filter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const quoteUrl = (quoteId) => `https://billing.joinnewton.com/quote/${quoteId}`;

  const copyUrl = (quoteId) => {
    navigator.clipboard.writeText(quoteUrl(quoteId));
  };

  return (
    <div style={{ maxWidth: embedded ? 1200 : 1000, margin: '0 auto', padding: embedded ? 0 : '30px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: embedded ? 20 : 28, fontWeight: 700, marginBottom: 4, color: '#1a1a1a' }}>Quotes</h1>
          <p style={{ color: '#666', fontSize: 14, margin: 0 }}>{quotes.length} quote{quotes.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'draft', 'sent', 'accepted'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: filter === f ? '#1E6434' : '#f3f4f6',
                color: filter === f ? '#fff' : '#6b7280',
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading quotes...</div>
      ) : quotes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No quotes found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {quotes.map(quote => {
            const colors = statusColors[quote.status] || statusColors.draft;
            return (
              <div
                key={quote.quoteId}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px',
                  background: '#fff', display: 'flex', alignItems: 'center', gap: 16,
                }}
              >
                {/* Status badge */}
                <span style={{
                  padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 20,
                  background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
                  textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
                }}>
                  {quote.status}
                </span>

                {/* Main info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                      {quote.organizationName || 'Unnamed'}
                    </span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{quote.quoteId}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    {quote.billingFirstName} {quote.billingLastName}
                    {quote.billingEmail && <span> · {quote.billingEmail}</span>}
                    <span> · {quote.quantity} location{quote.quantity !== 1 ? 's' : ''}</span>
                    <span> · {quote.contractTerm === 'annual' ? 'Annual' : 'Month-to-month'}</span>
                    {quote.signatureSentAt && (
                      <span style={{ color: '#047857', fontWeight: 600 }}> · Sent for signature {formatDate(quote.signatureSentAt)}</span>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{formatDate(quote.createdAt)}</div>
                  {quote.acceptedAt && <div style={{ fontSize: 11, color: '#16a34a' }}>Accepted {formatDate(quote.acceptedAt)}</div>}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {quote.status !== 'accepted' && (
                    onEdit ? (
                      <button
                        onClick={() => onEdit(quote)}
                        title="Load this quote into the Quote Builder above to edit and regenerate the contract"
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                          border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer',
                        }}
                      >
                        Edit
                      </button>
                    ) : (
                      <a
                        href={`/quoteBuilder?edit=${quote.quoteId}`}
                        title="Open this quote in the Quote Builder to edit and regenerate the contract"
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                          border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                          textDecoration: 'none', display: 'inline-block',
                        }}
                      >
                        Edit
                      </a>
                    )
                  )}
                  {quote.status !== 'accepted' && (
                    <button
                      onClick={() => openSendModal(quote)}
                      title="Email this quote for signature via onboarding@joinnewton.com"
                      style={{
                        padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                        border: 'none', background: '#0B4426', color: '#fff', cursor: 'pointer',
                      }}
                    >
                      {quote.signatureSentAt ? 'Resend' : 'Send'}
                    </button>
                  )}
                  <button
                    onClick={() => setViewerQuote(quote)}
                    title="View the MSA + Order Form + BAA PDF rendered from this quote"
                    style={{
                      padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                      border: '1px solid #1E6434', background: '#fff', color: '#1E6434', cursor: 'pointer',
                    }}
                  >
                    Contract
                  </button>
                  <button
                    onClick={() => copyUrl(quote.quoteId)}
                    style={{
                      padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                      border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer',
                    }}
                  >
                    Copy Link
                  </button>
                  <a
                    href={quoteUrl(quote.quoteId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                      border: 'none', background: '#1E6434', color: '#fff', cursor: 'pointer',
                      textDecoration: 'none', display: 'inline-block',
                    }}
                  >
                    View
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sendQuote && (
        <div
          onClick={() => !sendBusy && setSendQuote(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(460px, 94vw)', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ background: '#0B4426', padding: '14px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Send for Signature</div>
              <div style={{ fontSize: 11, color: '#cfe3d6', marginTop: 2 }}>
                {sendQuote.organizationName || sendQuote.quoteId} — emails the contract from onboarding@joinnewton.com; the signer reviews, signs, and sets up payment.
              </div>
            </div>
            <div style={{ padding: 18 }}>
              <input
                type="text" value={sendName} onChange={e => setSendName(e.target.value)} placeholder="Signer full name"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
              />
              <input
                type="text" value={sendTitle} onChange={e => setSendTitle(e.target.value)} placeholder="Title (e.g. Owner, DDS)"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
              />
              <input
                type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="signer@practice.com"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }}
              />
              {sendMsg && <div style={{ fontSize: 12, color: '#047857', fontWeight: 600, marginBottom: 10 }}>✓ {sendMsg}</div>}
              {sendErr && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{sendErr}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSendQuote(null)}
                  disabled={sendBusy}
                  style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}
                >
                  {sendMsg ? 'Done' : 'Cancel'}
                </button>
                <button
                  onClick={handleSendSignature}
                  disabled={sendBusy}
                  style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', background: sendBusy ? '#9ca3af' : '#0B4426', color: '#fff', cursor: sendBusy ? 'not-allowed' : 'pointer' }}
                >
                  {sendBusy ? 'Sending…' : (sendMsg ? 'Send Again' : 'Send for Signature')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewerQuote && (
        <ContractViewer
          url={`${GTM_API_URL}/api/v2/quotes/${viewerQuote.quoteId}/contract`}
          title={`${viewerQuote.organizationName || 'Quote'} — ${viewerQuote.quoteId}`}
          onClose={() => setViewerQuote(null)}
        />
      )}
    </div>
  );
};

export default QuotesList;
